'use client';

import { DivisionsSectionItemProps } from './DivisionsSectionItem.types';
import { css } from '@onbeam/business-styled-system/css';
import { AnimatePresence, motion } from 'motion/react';
import Image from 'next/image';
import { flex } from '@onbeam/business-styled-system/patterns';
import {
  ACTIVE_ITEM_WIDTH_REM,
  INACTIVE_ITEM_WIDTH_REM,
} from './DivisionsSectionCarousel.utils';
import { DivisionsSectionItemTimer } from './DivisionsSectionItemTimer';
import dynamic from 'next/dynamic';
import { useEffect, useRef } from 'react';
import { LottieRefCurrentProps } from 'lottie-react';

const Lottie = dynamic(() => import('lottie-react'), { ssr: false });

const MotionImage = motion.create(Image);

export const DivisionsSectionItem = ({
  name,
  image,
  transition,
  isActive,
  isVisible,
  isAtEnd,
  animationData,
  icon: Icon,
}: DivisionsSectionItemProps) => {
  const lottieRef = useRef<LottieRefCurrentProps>(null);

  useEffect(() => {
    if (!lottieRef.current || !isActive) return;
    lottieRef.current.goToAndPlay(0);
  }, [isActive]);

  return (
    <motion.div
      className={css({ pos: 'relative' })}
      initial={false}
      animate={{
        minWidth: isActive
          ? `${ACTIVE_ITEM_WIDTH_REM}rem`
          : `${INACTIVE_ITEM_WIDTH_REM}rem`,
        opacity: isVisible ? (isActive ? 1 : 0.8) : 0,
      }}
      transition={transition}
      data-active={isActive}
    >
      <div
        className={flex({
          pos: 'relative',
          align: 'center',
          justify: 'center',
        })}
      >
        <Image
          className={css({ rounded: 'xs' })}
          src={image}
          alt={`beam${name}`}
          width={280}
          height={280}
          quality={100}
          priority
        />
        <AnimatePresence>
          {isActive && !isAtEnd && <DivisionsSectionItemTimer />}
        </AnimatePresence>
        <Lottie
          lottieRef={lottieRef}
          animationData={animationData}
          style={{
            position: 'absolute',
            width: 64,
            height: 64,
          }}
          autoplay={false}
          loop={false}
        />
      </div>
      <MotionImage
        className={css({
          pos: 'absolute',
          px: '3',
          top: '8',
          zIndex: '-1',
          opacity: 0.7,
          filter: '[blur(14px)]',
        })}
        src={image}
        alt=""
        width={280}
        height={280}
        initial={false}
        animate={{
          opacity: isActive ? 0.7 : 0,
        }}
        transition={transition}
      />
      <div className={flex({ mt: '6' })}>
        <Icon height={14} />
      </div>
    </motion.div>
  );
};
